import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Empty,
  Modal,
  Progress,
  Skeleton,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { CheckOutlined, FlagOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { PageTitle, StatCard } from '../components/ds';
import { openingApi } from '../lib/wikiApi';

type DashboardRow = Awaited<ReturnType<typeof openingApi.dashboard>>[number];

/**
 * Дашборд УК по открытию точек (Q18). Таблица всех партнёров с прогрессом +
 * сводный CSS-Гант (одна строка на партнёра). Требует kb:opening_dashboard —
 * при 403 показываем сообщение о доступе. Маршрут: /admin/opening.
 *
 * Гант — на чистом CSS (без frappe-gantt): полоса от startedAt до
 * confirmed/proposed даты, заливка = progressPercent, вертикаль «сегодня».
 */
export function OpeningDashboardPage(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki', 'opening', 'dashboard'],
    queryFn: () => openingApi.dashboard(),
    retry: false,
  });

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error) {
    const status = (error as { status?: number }).status;
    if (status === 403) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Нет доступа"
          description="Дашборд открытия точек доступен только сотрудникам УК (право kb:opening_dashboard)."
        />
      );
    }
    return <Alert type="error" showIcon message="Не удалось загрузить" description={(error as Error).message} />;
  }

  const rows = data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageTitle
        title="Открытия точек"
        sub={
          rows.length
            ? `${rows.filter((r) => r.progressPercent < 100).length} в работе · ${rows.filter((r) => r.isOverdue).length} с просрочкой · ${
                rows.filter((r) => r.daysSinceProgress >= 3 && r.progressPercent < 100).length
              } без движения 3+ дня`
            : 'Прогресс всех партнёров, согласование дат и контроль просрочек'
        }
        style={{ marginBottom: 0 }}
      />

      {rows.length === 0 ? (
        <Empty description="Пока ни один партнёр не запустил открытие точки" />
      ) : (
        <>
          <SummaryStats rows={rows} />
          <GanttChart rows={rows} />
          <DashboardTable rows={rows} />
        </>
      )}
    </div>
  );
}

function SummaryStats({ rows }: { rows: DashboardRow[] }): JSX.Element {
  const active = rows.filter((r) => r.progressPercent < 100).length;
  const overdue = rows.filter((r) => r.isOverdue).length;
  const stalled = rows.filter((r) => r.daysSinceProgress >= 3 && r.progressPercent < 100).length;
  const done = rows.filter((r) => r.progressPercent >= 100).length;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
      <StatCard label="Открытий в работе" value={active} tone="blue" icon={<FlagOutlined />} hint={done ? `завершено: ${done}` : undefined} />
      <StatCard label="Просрочено" value={overdue} tone="red" hint={overdue ? 'срок открытия прошёл' : 'всё в сроке'} hintColor={overdue ? 'var(--fk-error)' : undefined} />
      <StatCard label="Без движения 3+ дня" value={stalled} tone="orange" hint="ни одного шага за 3 дня" />
    </div>
  );
}

// ─── CSS-Гант ───────────────────────────────────────────────────────

function GanttChart({ rows }: { rows: DashboardRow[] }): JSX.Element {
  const { rangeStart, rangeEnd, months, todayPct } = useMemo(() => {
    const starts = rows.map((r) => dayjs(r.startedAt));
    const ends = rows.map((r) =>
      dayjs(r.confirmedOpenDate ?? r.proposedOpenDate ?? dayjs(r.startedAt).add(60, 'day').toISOString()),
    );
    let min = starts.reduce((a, b) => (a.isBefore(b) ? a : b), starts[0]).startOf('month');
    let max = ends.reduce((a, b) => (a.isAfter(b) ? a : b), ends[0]).endOf('month');
    const today = dayjs();
    if (today.isBefore(min)) min = today.startOf('month');
    if (today.isAfter(max)) max = today.endOf('month');
    const total = Math.max(1, max.diff(min, 'day'));
    // Метки месяцев.
    const monthList: Array<{ label: string; leftPct: number }> = [];
    let cur = min.startOf('month');
    while (cur.isBefore(max)) {
      monthList.push({
        label: cur.format('MMM YY'),
        leftPct: (cur.diff(min, 'day') / total) * 100,
      });
      cur = cur.add(1, 'month');
    }
    return {
      rangeStart: min,
      rangeEnd: max,
      months: monthList,
      todayPct: (today.diff(min, 'day') / total) * 100,
    };
  }, [rows]);

  const totalDays = Math.max(1, rangeEnd.diff(rangeStart, 'day'));

  return (
    <div style={{ background: 'var(--fk-surface)', border: '1px solid var(--fk-border)', borderRadius: 8, padding: 16, overflowX: 'auto' }}>
      <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>Сводный Гант</h2>
      <div style={{ minWidth: 640 }}>
        {/* Шкала месяцев */}
        <div style={{ position: 'relative', height: 20, marginLeft: 180, marginBottom: 6, borderBottom: '1px solid var(--fk-border)' }}>
          {months.map((m) => (
            <span
              key={m.label}
              style={{ position: 'absolute', left: `${m.leftPct}%`, fontSize: 11, color: 'var(--fk-text-muted)', transform: 'translateX(2px)' }}
            >
              {m.label}
            </span>
          ))}
        </div>

        {rows.map((r) => {
          const start = dayjs(r.startedAt);
          const end = dayjs(r.confirmedOpenDate ?? r.proposedOpenDate ?? start.add(60, 'day').toISOString());
          const leftPct = (start.diff(rangeStart, 'day') / totalDays) * 100;
          const widthPct = Math.max(2, (end.diff(start, 'day') / totalDays) * 100);
          const barColor = r.isOverdue ? 'var(--fk-error)' : r.progressPercent >= 100 ? 'var(--fk-success)' : 'var(--fk-orange)';
          return (
            <div key={r.partnerId} style={{ display: 'flex', alignItems: 'center', height: 30 }}>
              <div style={{ width: 180, flexShrink: 0, fontSize: 12, paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.partnerName}
              </div>
              <div style={{ position: 'relative', flex: 1, height: 18, background: 'var(--fk-bg)', borderRadius: 4 }}>
                <Tooltip
                  title={`${start.format('DD.MM')} → ${end.format('DD.MM.YYYY')} · ${r.progressPercent}%`}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      top: 0,
                      height: 18,
                      background: `${barColor}30`,
                      border: `1px solid ${barColor}`,
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ width: `${r.progressPercent}%`, height: '100%', background: barColor, opacity: 0.55 }} />
                  </div>
                </Tooltip>
                {/* Линия «сегодня» */}
                {todayPct >= 0 && todayPct <= 100 && (
                  <div style={{ position: 'absolute', left: `${todayPct}%`, top: -2, height: 22, width: 2, background: 'var(--fk-indigo)' }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--fk-text-muted)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--fk-orange)', borderRadius: 2, marginRight: 4 }} /> В работе</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--fk-error)', borderRadius: 2, marginRight: 4 }} /> Просрочено</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--fk-success)', borderRadius: 2, marginRight: 4 }} /> Открыто</span>
        <span><span style={{ display: 'inline-block', width: 2, height: 12, background: 'var(--fk-indigo)', marginRight: 4, verticalAlign: 'middle' }} /> Сегодня</span>
      </div>
    </div>
  );
}

// ─── Таблица ────────────────────────────────────────────────────────

function DashboardTable({ rows }: { rows: DashboardRow[] }): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [confirmFor, setConfirmFor] = useState<DashboardRow | null>(null);
  const [confirmDate, setConfirmDate] = useState<dayjs.Dayjs | null>(null);

  const confirmMutation = useMutation({
    mutationFn: () => openingApi.confirmDate(confirmFor!.partnerId, confirmDate?.format('YYYY-MM-DD')),
    onSuccess: async () => {
      void message.success('Дата подтверждена');
      await qc.invalidateQueries({ queryKey: ['wiki', 'opening', 'dashboard'] });
      setConfirmFor(null);
    },
    onError: (e: Error) => void message.error(e.message),
  });

  const completeMutation = useMutation({
    mutationFn: (partnerId: string) => openingApi.complete(partnerId),
    onSuccess: async () => {
      void message.success('Открытие завершено');
      await qc.invalidateQueries({ queryKey: ['wiki', 'opening', 'dashboard'] });
    },
    onError: (e: Error) => void message.error(e.message),
  });

  return (
    <>
      <div className="fk-ds-table">
      <Table<DashboardRow>
        rowKey="partnerId"
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Партнёр',
            dataIndex: 'partnerName',
            render: (v: string, r) => (
              <div>
                <b>{v}</b>
                {r.cityName && <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>{r.cityName}</div>}
              </div>
            ),
          },
          {
            title: 'Прогресс',
            dataIndex: 'progressPercent',
            width: 180,
            render: (v: number, r) => (
              <div>
                <Progress percent={v} size="small" strokeColor={r.isOverdue ? 'var(--fk-error)' : 'var(--fk-orange)'} />
                <span style={{ fontSize: 11, color: 'var(--fk-text-muted)' }}>
                  {r.completedSteps}/{r.totalSteps} шагов
                </span>
              </div>
            ),
          },
          {
            title: 'Дата открытия',
            width: 150,
            render: (_: unknown, r) =>
              r.confirmedOpenDate ? (
                <Tag color="green">{dayjs(r.confirmedOpenDate).format('DD.MM.YYYY')}</Tag>
              ) : r.proposedOpenDate ? (
                <Tooltip title="Предложена партнёром, не подтверждена УК">
                  <Tag color="orange">{dayjs(r.proposedOpenDate).format('DD.MM.YYYY')} (?)</Tag>
                </Tooltip>
              ) : (
                <span style={{ color: 'var(--fk-text-muted)' }}>—</span>
              ),
          },
          {
            title: 'Осталось',
            dataIndex: 'daysLeft',
            width: 110,
            render: (v: number | null) =>
              v === null ? (
                <span style={{ color: 'var(--fk-text-muted)' }}>—</span>
              ) : v < 0 ? (
                <Tag color="red">просрочено {-v} дн</Tag>
              ) : (
                <span>{v} дн</span>
              ),
          },
          {
            title: 'Без движения',
            dataIndex: 'daysSinceProgress',
            width: 120,
            render: (v: number) =>
              v >= 3 ? <Tag color="orange">{v} дн</Tag> : <span style={{ color: 'var(--fk-text-muted)' }}>{v} дн</span>,
          },
          {
            title: '',
            width: 220,
            render: (_: unknown, r) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Button
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => {
                    setConfirmFor(r);
                    setConfirmDate(r.proposedOpenDate ? dayjs(r.proposedOpenDate) : null);
                  }}
                >
                  Подтвердить дату
                </Button>
                {r.progressPercent >= 100 && (
                  <Button size="small" icon={<FlagOutlined />} onClick={() => completeMutation.mutate(r.partnerId)}>
                    Завершить
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      </div>

      <Modal
        open={confirmFor != null}
        title={`Подтвердить дату открытия — ${confirmFor?.partnerName ?? ''}`}
        onCancel={() => setConfirmFor(null)}
        onOk={() => confirmMutation.mutate()}
        confirmLoading={confirmMutation.isPending}
        okText="Подтвердить"
        cancelText="Отмена"
        okButtonProps={{ style: { background: 'var(--fk-orange)', borderColor: 'var(--fk-orange)' } }}
      >
        <p style={{ color: 'var(--fk-text-2)' }}>
          Зафиксирует дату открытия. Если оставить пусто — подтвердится дата, предложенная партнёром.
        </p>
        <DatePicker value={confirmDate} onChange={setConfirmDate} format="DD.MM.YYYY" style={{ width: '100%' }} placeholder="Дата открытия" />
      </Modal>
    </>
  );
}
